import { supabase } from "../../supabaseClient";
import { notify } from "../../components/UI/DesignStandards/Notifications/notify";

// Session management utilities
// Configure idle timeout via env. If 0/undefined, idle sign-out is disabled by default.
// Example to enable for 60 minutes: REACT_APP_IDLE_LOGOUT_MINUTES=60
const IDLE_LOGOUT_MINUTES = Number(
  process.env.REACT_APP_IDLE_LOGOUT_MINUTES ?? 0
);
const SESSION_TIMEOUT =
  IDLE_LOGOUT_MINUTES > 0 ? IDLE_LOGOUT_MINUTES * 60 * 1000 : null; // milliseconds
let lastActivity = Date.now();
let sessionTimeoutId = null;

const resetSessionTimer = () => {
  // If idle logout is disabled, do nothing
  if (!SESSION_TIMEOUT) return;

  lastActivity = Date.now();
  if (sessionTimeoutId) {
    clearTimeout(sessionTimeoutId);
  }
  sessionTimeoutId = setTimeout(handleSessionTimeout, SESSION_TIMEOUT);
};

// Update last activity timestamp
export const updateLastActivity = () => {
  lastActivity = Date.now();
};

// Check if session has expired
export const isSessionExpired = () => {
  if (!SESSION_TIMEOUT) return false;
  return Date.now() - lastActivity > SESSION_TIMEOUT;
};

// Clear sensitive data from session storage
export const clearSessionData = () => {
  // Clear any local storage or session storage data
  localStorage.removeItem("user");
  sessionStorage.clear();
};

// Handle session timeout
export const handleSessionTimeout = async () => {
  try {
    // Check if user is signed in
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) return; // Don't proceed if user is not signed in

    // Sign out the user
    const { error } = await supabase.auth.signOut();
    if (error) throw error;

    // Clear any local session data
    clearSessionData();

    // Store the timeout message in sessionStorage so sign-in can show context (only if enabled)
    if (SESSION_TIMEOUT) {
      sessionStorage.setItem("sessionTimeoutMessage", "true");
    }

    // Redirect to login page
    window.location.href = "/signin";
  } catch (error) {
    console.error("Error during session timeout:", error);
    if (SESSION_TIMEOUT) {
      sessionStorage.setItem("sessionTimeoutMessage", "error");
    }
    window.location.href = "/signin";
  }
};

// Check and show timeout message
export const checkAndShowTimeoutMessage = () => {
  if (!SESSION_TIMEOUT) return; // Do not show idle-timeout toast if feature is disabled
  const timeoutMessage = sessionStorage.getItem("sessionTimeoutMessage");
  if (timeoutMessage === "true") {
    // Use setTimeout to ensure the toast is shown after the page is fully loaded
    setTimeout(() => {
      notify.warning(
        "You have been logged out due to inactivity. Please sign in again.",
        {
          autoClose: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          closeButton: true,
          style: {
            width: "400px",
            fontSize: "1rem",
            padding: "1rem",
          },
        }
      );
    }, 100);
    sessionStorage.removeItem("sessionTimeoutMessage");
  } else if (timeoutMessage === "error") {
    setTimeout(() => {
      notify.error("Error during logout. Please try signing in again.", {
        autoClose: false,
        closeOnClick: false,
      });
    }, 100);
    sessionStorage.removeItem("sessionTimeoutMessage");
  }
};

// Set up session monitoring
export const setupSessionMonitoring = () => {
  // If idle logout is disabled, no-op and return a cleanup that does nothing
  if (!SESSION_TIMEOUT) {
    return () => {};
  }
  // Set up activity listeners
  const activityEvents = [
    "mousedown",
    "mousemove",
    "keypress",
    "scroll",
    "touchstart",
  ];
  activityEvents.forEach((event) => {
    document.addEventListener(event, resetSessionTimer, { passive: true });
  });

  // Initial timer setup
  resetSessionTimer();

  // Cleanup function
  return () => {
    activityEvents.forEach((event) => {
      document.removeEventListener(event, resetSessionTimer);
    });
    if (sessionTimeoutId) {
      clearTimeout(sessionTimeoutId);
    }
  };
};
